import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { gruposItensApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { termosLegiveis } from './rotulosItens'
import type { GrupoSugerido } from '../../types'

/** "aceita: LEITE e PO" / "recusa: CONDENSADO"; várias regras que aceitam são alternativas ("ou"). */
function regrasTexto(g: GrupoSugerido): string {
  const aceita = g.regras.filter((r) => r.tipo === 1).map((r) => termosLegiveis(r.termos).replace(/ \+ /g, ' e '))
  const recusa = g.regras.filter((r) => r.tipo === 2).map((r) => termosLegiveis(r.termos).replace(/ \+ /g, ' e '))
  return [aceita.length ? `aceita: ${aceita.join(' ou ')}` : '', recusa.length ? `recusa: ${recusa.join(', ')}` : ''].filter(Boolean).join(' · ')
}

/** Catálogo de grupos prontos: marca os que quer criar; depois oferece agrupar o histórico. */
export function GruposSugeridosModal({ onClose, onMudou }: { onClose: () => void; onMudou: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [desmarcadas, setDesmarcadas] = useState<Set<string>>(new Set())
  const [criados, setCriados] = useState<number | null>(null)

  const { data: sugeridos = [], isLoading, isError, error } = useQuery({ queryKey: ['itens-grupos-sugeridos'], queryFn: gruposItensApi.sugeridos })

  // Todos os que ainda não existem começam marcados; guardamos só o que o usuário desmarcou.
  const novos = sugeridos.filter((g) => !g.jaExiste)
  const sel = new Set(novos.filter((g) => !desmarcadas.has(g.chave)).map((g) => g.chave))
  const alternar = (chave: string) => setDesmarcadas((s) => { const n = new Set(s); if (n.has(chave)) n.delete(chave); else n.add(chave); return n })

  const criar = useMutation({
    mutationFn: () => gruposItensApi.criarSugeridos([...sel]),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['itens-grupos-sugeridos'] })
      onMudou()
      setCriados(r.criados)
    },
    onError: (e: Error) => toast.error(e.message),
  })
  const agrupar = useMutation({
    mutationFn: gruposItensApi.reprocessarHistorico,
    onSuccess: (r) => { onMudou(); toast.success(`Histórico agrupado: ${r.agrupados} itens com grupo, ${r.semGrupo} sem grupo.`); onClose() },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal
      open onClose={onClose} title="Grupos sugeridos" size="lg"
      footer={criados !== null
        ? <><Button variant="light" onClick={onClose}>Fechar</Button><Button icon="ri-refresh-line" loading={agrupar.isPending} onClick={() => agrupar.mutate()}>Agrupar histórico agora</Button></>
        : <><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-add-line" disabled={sel.size === 0} loading={criar.isPending} onClick={() => criar.mutate()}>Criar {sel.size} {sel.size === 1 ? 'grupo' : 'grupos'}</Button></>}
    >
      {criados !== null ? (
        <div className="space-y-2">
          <p className="flex items-center gap-1.5 text-white"><i className="ri-checkbox-circle-line text-income" aria-hidden /> {criados} {criados === 1 ? 'grupo criado' : 'grupos criados'}</p>
          <p className="text-13 text-muted">Os grupos novos ainda não valem para as notas que você já importou. Agrupe o histórico para aplicá-los; correções manuais suas são mantidas.</p>
        </div>
      ) : isLoading ? <LoadingState /> : isError ? <ErrorState message={(error as Error | null)?.message ?? 'Não consegui carregar as sugestões.'} /> : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-13 text-muted">Grupos prontos com regras já ajustadas. Desmarque o que não quiser; depois você pode editar qualquer um.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="light" onClick={() => setDesmarcadas(new Set())}>Marcar todos</Button>
              <Button size="sm" variant="light" onClick={() => setDesmarcadas(new Set(novos.map((g) => g.chave)))}>Limpar</Button>
            </div>
          </div>
          <ul className="space-y-2">
            {sugeridos.map((g) => (
              <li key={g.chave}>
                <label className={`flex items-start gap-3 rounded bg-surface2 px-3 py-2 ${g.jaExiste ? 'opacity-60' : 'cursor-pointer'}`}>
                  <input type="checkbox" className="mt-1 h-4 w-4 accent-primary" checked={!g.jaExiste && sel.has(g.chave)} disabled={g.jaExiste} onChange={() => alternar(g.chave)} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-white">{g.nome}{g.jaExiste && <Badge variant="muted" className="ml-2">já existe</Badge>}</span>
                    <span className="block text-2xs text-muted">{regrasTexto(g)}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  )
}
